import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { getQuestionsByCategory, getQuestionsByCategorySlug } from '../services/api';

interface Question {
  _id: string;
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

const optionLetters = ['A', 'B', 'C', 'D'];

const QuizPage = () => {
  const params = useParams<{ subjectSlug?: string; questionSlug?: string; subjectId?: string }>();
  const { subjectSlug, questionSlug, subjectId } = params;
  const navigate = useNavigate();
  
  // Handle different URL formats
  let actualSubjectSlug: string | undefined;
  let actualQuestionSlug: string | undefined;
  let useSlugApi: boolean = false;
  
  if (subjectSlug && questionSlug) {
    // New format: /free-agriculture-quiz/:subjectSlug/:questionSlug
    actualSubjectSlug = subjectSlug;
    actualQuestionSlug = questionSlug;
    useSlugApi = true;
  } else if (subjectSlug && subjectId) {
    // Old format: /free-agriculture-quiz/:subjectSlug/:subjectId
    actualSubjectSlug = subjectSlug;
    actualQuestionSlug = subjectId; // In this case, subjectId is actually the questionSlug
    useSlugApi = true;
  } else if (subjectSlug) {
    // Format: /free-agriculture-quiz/:subjectSlug (category slug only)
    actualSubjectSlug = subjectSlug;
    actualQuestionSlug = undefined;
    useSlugApi = true;
  } else if (subjectId && !subjectId.match(/^[0-9a-fA-F]{24}$/)) {
    // Format: /free-agriculture-quiz/:subjectId where subjectId is actually a slug (not ObjectId)
    actualSubjectSlug = subjectId;
    actualQuestionSlug = undefined;
    useSlugApi = true;
  } else {
    // Fallback: /free-agriculture-quiz/:subjectId (ObjectId only)
    actualSubjectSlug = undefined;
    actualQuestionSlug = subjectId || '';
    useSlugApi = false;
  }
  
  // Convert slug back to readable category name
  const getCategoryNameFromSlug = (slug: string) => {
    return slug
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };
  
  const categoryName = subjectSlug ? getCategoryNameFromSlug(subjectSlug) : 'This Subject';
  
  const [questions, setQuestions] = useState<Question[]>([]);
  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [score, setScore] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (useSlugApi && actualSubjectSlug) {
      fetchQuestionsBySlug(actualSubjectSlug);
    } else if (!useSlugApi && actualQuestionSlug) {
      fetchQuestions(actualQuestionSlug);
    }
  }, [useSlugApi, actualSubjectSlug, actualQuestionSlug]);

  // Handle question slug navigation
  useEffect(() => {
    if (questions.length > 0) {
      if (actualQuestionSlug) {
        // Find the question by slug
        const questionIndex = questions.findIndex(q => {
          const questionSlug = q.question
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .substring(0, 100)
            .trim();
          return questionSlug === actualQuestionSlug;
        });
        
        if (questionIndex !== -1) {
          setCurrent(questionIndex);
        } else {
          // If question not found, redirect to first question
          const firstQuestionSlug = questions[0].question
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .substring(0, 100)
            .trim();
          navigate(`/free-agriculture-quiz/${subjectSlug}/${firstQuestionSlug}`, { replace: true });
        }
      } else if (actualSubjectSlug) {
        // If we have a subject slug but no question slug, redirect to first question
        const firstQuestionSlug = questions[0].question
          .toLowerCase()
          .replace(/[^a-z0-9\s-]/g, '')
          .replace(/\s+/g, '-')
          .replace(/-+/g, '-')
          .substring(0, 100)
          .trim();
        navigate(`/free-agriculture-quiz/${actualSubjectSlug}/${firstQuestionSlug}`, { replace: true });
      }
    }
  }, [questions, actualQuestionSlug, actualSubjectSlug, navigate, subjectSlug]);

  const fetchQuestions = async (categoryId: string) => {
    setLoading(true);
    const res = await getQuestionsByCategory(categoryId);
    setQuestions(Array.isArray(res) ? res : []);
    setCurrent(0);
    setSelected(null);
    setShowAnswer(false);
    setLoading(false);
  };

  const fetchQuestionsBySlug = async (categorySlug: string) => {
    setLoading(true);
    try {
      const res = await getQuestionsByCategorySlug(categorySlug);
      setQuestions(Array.isArray(res) ? res : []);
      setCurrent(0);
      setSelected(null);
      setShowAnswer(false);
    } catch (error) {
      console.error('Error fetching questions by slug:', error);
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  if (!actualSubjectSlug && !actualQuestionSlug) {
    return <div className="max-w-2xl mx-auto mt-12 text-center text-lg">No quiz found for this subject.</div>;
  }

  if (loading) {
    return <div className="max-w-2xl mx-auto mt-12 text-center text-lg">Loading questions...</div>;
  }

  if (questions.length === 0) {
    return (
      <div className="max-w-2xl mx-auto mt-12 text-center">
        <div className="bg-white dark:bg-gray-800 rounded-[24px] p-8 shadow-lg">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            No Questions Found
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            {actualSubjectSlug 
              ? `No questions found for "${getCategoryNameFromSlug(actualSubjectSlug)}". This category might not exist or might not have any questions yet.`
              : 'No questions found for this quiz.'
            }
          </p>
          <button
            onClick={() => navigate('/free-agriculture-quiz')}
            className="px-6 py-3 bg-green-800 hover:bg-green-900 text-white rounded-[24px] transition-colors duration-200"
          >
            Browse Available Categories
          </button>
        </div>
      </div>
    );
  }

  if (showResults) {
    return (
      <div className="min-h-screen bg-[#f6faf7] py-8">
        <div className="w-full max-w-4xl mx-auto bg-white dark:bg-gray-800 rounded-[24px] shadow-2xl p-6 border border-gray-100 px-4 sm:px-6 md:px-8">
          {/* Celebration Header */}
          <div className="text-center mb-4">
            <h1 className="text-3xl font-bold text-green-600 mb-2">Congratulations! 🎉</h1>
          </div>

          {/* Results */}
          <div className="bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 rounded-[24px] p-6 mb-4">
            <div className="text-center">
              <div className="text-5xl font-bold text-green-600 mb-2">
                {score}/{questions.length}
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-[24px] p-6">
            <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-3">
              Ready for More Challenges?
            </h2>
            <p className="text-base text-gray-600 dark:text-gray-300 mb-4">
              Unlock our premium test series with thousands of questions, detailed explanations, and performance analytics!
            </p>
            <button className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-4 px-8 rounded-[24px] text-lg transition-all duration-200 transform hover:scale-105 shadow-lg" aria-label="Buy Test Series Now">
              Buy Test Series Now! 🚀
            </button>
          </div>
        </div>

        {/* Action Buttons Below Card */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mt-6">
          <button
            onClick={() => {
              setShowResults(false);
              setCurrent(0);
              setSelected(null);
              setShowAnswer(false);
            }}
            className="bg-green-800 hover:bg-green-900 text-white font-bold py-3 px-8 rounded-[24px] transition-colors duration-200 text-base"
            aria-label="Take Quiz Again"
          >
            Take Quiz Again
          </button>
          <button
            onClick={() => {
              if (!import.meta.env.SSR && typeof window !== 'undefined') {
                window.history.back();
              }
            }}
            className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-3 px-8 rounded-[24px] transition-colors duration-200 text-base"
            aria-label="Back to Subjects"
          >
            Back to Subjects
          </button>
        </div>
      </div>
    );
  }

  const q = questions[current];

  const handleOptionClick = (idx: number) => {
    setSelected(idx);
    setShowAnswer(true);
  };

  const navigateToQuestion = (questionIndex: number) => {
    const question = questions[questionIndex];
    const questionSlug = question.question
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .substring(0, 100)
      .trim();
    navigate(`/free-agriculture-quiz/${subjectSlug}/${questionSlug}`);
  };

  const handleNext = () => {
    if (current === questions.length - 1) {
      // Calculate score
      const correctAnswers = questions.filter((_q, _idx) => {
        // For now, we'll assume all answered questions are correct
        // In a real implementation, you'd track user answers
        return true;
      }).length;
      setScore(correctAnswers);
      setShowResults(true);
    } else {
      const nextIndex = current + 1;
      setCurrent(nextIndex);
      setSelected(null);
      setShowAnswer(false);
      // Navigate to the next question URL
      navigateToQuestion(nextIndex);
    }
  };

  return (
    <div className="min-h-[80vh] bg-[#f6faf7] py-12">
      {/* Quiz Section */}
      <div className="w-full max-w-7xl mx-auto bg-white dark:bg-gray-800 rounded-3xl shadow-2xl p-10 border border-gray-100 px-4 sm:px-8 md:px-16 lg:px-24 mb-8">
        {/* Question Number */}
        <div className="mb-8 flex justify-between items-center">
          <span className="text-base text-gray-500 font-semibold">Question {current + 1}</span>
        </div>

        {/* Question */}
        <div className="mb-10">
          {/* Optionally, you can fetch and show category name here */}
          <div className="text-xl md:text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2 text-left">{q.question}</div>
        </div>

        {/* Options */}
        <div className="space-y-5 mb-10">
          {q.options.map((opt, idx) => {
            let optionState = '';
            if (showAnswer) {
              if (idx === q.correctAnswer) optionState = 'correct';
              else if (selected === idx) optionState = 'wrong';
              else optionState = 'disabled';
            }
            return (
              <button
                key={idx}
                onClick={() => handleOptionClick(idx)}
                disabled={showAnswer}
                className={`w-full flex items-center px-5 py-4 rounded-[24px] border text-lg font-medium transition-all duration-200 shadow-sm
                  ${optionState === 'correct' ? 'bg-green-50 border-green-500 text-green-800' : ''}
                  ${optionState === 'wrong' ? 'bg-red-50 border-red-500 text-red-800' : ''}
                  ${optionState === 'disabled' ? 'bg-gray-100 border-gray-200 text-gray-400' : ''}
                  ${!showAnswer ? 'bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700 hover:bg-green-50 dark:hover:bg-green-900 hover:border-green-400' : ''}
                `}
                aria-label={`Select Option ${optionLetters[idx]}`}
              >
                <span className={`flex items-center justify-center h-9 w-9 rounded-full mr-5 text-base font-bold border-2
                  ${optionState === 'correct' ? 'bg-green-500 text-white border-green-500' : ''}
                  ${optionState === 'wrong' ? 'bg-red-500 text-white border-red-500' : ''}
                  ${optionState === 'disabled' ? 'bg-gray-300 text-white border-gray-300' : ''}
                  ${!showAnswer ? 'bg-gray-200 text-gray-700 border-gray-300' : ''}
                `}>
                  {optionLetters[idx]}
                </span>
                {opt}
              </button>
            );
          })}
        </div>

        {/* Next Button */}
        <div className="flex justify-center mb-6">
          <button
            onClick={handleNext}
            disabled={!showAnswer}
            className="px-10 py-4 rounded-[24px] text-white text-lg font-bold transition-all duration-200
              bg-green-600 hover:bg-green-700 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Next Question"
          >
            {current === questions.length - 1 ? 'Finish' : 'Next Question'}
          </button>
        </div>

        {/* Explanation */}
        {showAnswer && q.explanation.length > 1 && (
          <div className="mt-4 p-4 bg-blue-50 border-l-4 border-blue-400 text-blue-900 rounded">
            <strong>Explanation:</strong> {q.explanation}
          </div>
        )}
      </div>

      {/* All Questions Section */}
      <div className="w-full max-w-7xl mx-auto bg-white dark:bg-gray-800 rounded-[24px] shadow-2xl p-10 border border-gray-100 px-4 sm:px-8 md:px-16 lg:px-24">
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">
            More Questions from {categoryName}
          </h2>
          <p className="text-gray-600 dark:text-gray-300">
            Review all questions related to this subject below
          </p>
        </div>

        <div className="space-y-8">
          {questions.map((question, index) => (
            <div 
              key={question._id} 
              className="border border-gray-200 dark:border-gray-700 rounded-[24px] p-6 bg-gray-50 dark:bg-gray-900 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 hover:border-green-300 dark:hover:border-green-600 transition-all duration-200 hover:shadow-md"
              onClick={() => {
                // Navigate to the specific question URL
                navigateToQuestion(index);
                // Scroll to top of quiz section
                if (!import.meta.env.SSR && typeof window !== 'undefined') {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="h-8 w-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                    Q.
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                    {question.question}
                  </h3>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default QuizPage; 